import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Pause, AlertTriangle } from 'lucide-react';
import { http } from '../services/http';
import { Tour, TourButton, TourStep } from '../components/Tour';

/** GET /api/ai/status — 서버 공용 grk-ai 상태 */
interface AiStatus {
  configured: boolean;
  connected: boolean;
  running?: number;
  queued?: number;
  max?: number;
  paused?: boolean;
  error?: string;
  checkedAt: string;
}

type Range = 'month' | '30' | '90';

/** GET /api/ai/usage — 사람별 값은 없다 */
interface Usage {
  range: Range;
  totals: { calls: number; failed: number; inputTokens: number; outputTokens: number; avgMs: number | null };
  daily: { day: string; calls: number; failed: number }[];
  byPurpose: { purpose: string; calls: number; failed: number }[];
  failures: { id: number; at: string; purpose: string; error: string | null }[];
}

const RANGE_LABEL: Record<Range, string> = { month: '이번 달', '30': '최근 30일', '90': '최근 90일' };

/** 기능 이름 — investment_ai_calls.purpose */
const PURPOSE_LABEL: Record<string, string> = {
  'document.extract': '재무제표 추출',
  'ai.ping': '키 시험(예전)',
};
const purposeLabel = (p: string) => PURPOSE_LABEL[p] ?? p;

const num = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('ko-KR'));
/** 410000 → 410K */
const short = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n));
const when = (iso: string) => {
  const d = new Date(iso);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

type State = 'ok' | 'pause' | 'err';
const stateOf = (s: AiStatus): State => (s.connected && !s.paused ? 'ok' : s.connected && s.paused ? 'pause' : 'err');

const STATE_VIEW: Record<State, { label: string; ring: string; text: string; why: string; icon: React.ReactNode }> = {
  ok: { label: '정상', ring: 'bg-emerald-50 text-emerald-600', text: 'text-emerald-600', why: '', icon: <Check size={20} /> },
  pause: {
    label: '일시정지', ring: 'bg-amber-50 text-amber-600', text: 'text-amber-600', icon: <Pause size={20} />,
    why: '관리자가 AI 를 잠시 멈췄습니다. AI 기능(재무제표 추출)은 다시 켜질 때까지 쓸 수 없습니다. 급하면 관리자에게 문의하세요.',
  },
  err: {
    label: '오류', ring: 'bg-red-50 text-red-600', text: 'text-red-600', icon: <AlertTriangle size={20} />,
    why: 'AI 서버가 응답하지 않거나 연결 설정이 없습니다. 잠시 뒤 「다시 확인」을 눌러 보고, 계속되면 관리자에게 문의하세요.',
  },
};

/**
 * AI 관리 — 서버 공용 AI(grk-ai) 상태와 이 앱의 사용량(2026-10-27, project 와 같은 화면).
 * 키를 넣거나 고르는 칸은 없다. 사람별 사용량도 보이지 않는다.
 */
export const AiPage: React.FC = () => {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [checking, setChecking] = useState(false);
  const [range, setRange] = useState<Range>('month');
  const [usage, setUsage] = useState<Usage | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [tourRun, setTourRun] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      setStatus(await http.get<AiStatus>('/ai/status'));
    } catch (e) {
      setStatus({ configured: false, connected: false, error: (e as Error).message, checkedAt: new Date().toISOString() });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    setUsageError(null);
    http.get<Usage>('/ai/usage', { range }).then(setUsage).catch((e) => setUsageError((e as Error).message));
  }, [range]);

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'status', target: '[data-tour="ai-status"]', title: 'AI 상태', content: '회사 서버의 공용 AI 가 지금 일을 받는지 보여 줍니다. 일시정지 · 오류면 재무제표 추출이 실패합니다.' },
    { name: 'usage', target: '[data-tour="ai-usage"]', title: '사용량', content: '이 앱에서 부른 AI 호출 수 · 실패 · 토큰 · 걸린 시간입니다. 기능별로 나눠 봅니다.' },
    { name: 'fail', target: '[data-tour="ai-fail"]', title: '최근 실패', content: '실패한 호출 10건과 원인입니다. 같은 원인이 반복되면 관리자에게 알려 주세요.' },
  ], []);

  const st = status ? stateOf(status) : null;
  const view = st ? STATE_VIEW[st] : null;
  const detail = !status ? '' : st === 'ok'
    ? `처리 중 ${status.running ?? 0} / ${status.max ?? '?'} · 대기 ${status.queued ?? 0}`
    : st === 'pause' ? '새 요청을 받지 않습니다'
    : status.configured ? 'AI 서버에 연결할 수 없습니다' : 'AI 연결 설정이 없습니다';

  const t = usage?.totals;
  const maxDay = Math.max(1, ...(usage?.daily ?? []).map((d) => d.calls));
  const maxPurpose = Math.max(1, ...(usage?.byPurpose ?? []).map((p) => p.calls));

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">AI 관리 <TourButton onClick={() => setTourRun(true)} /></h1>
          <p className="text-sm text-slate-500 mt-0.5">서버 공용 AI(grk-ai) 상태와 이 앱의 사용량</p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {/* AI 상태 */}
        <section data-tour="ai-status" className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-slate-900">AI 상태</h2>
            <div className="ml-auto flex items-center gap-2">
              {status && <span className="text-xs text-slate-500 tabular-nums">{when(status.checkedAt)} 확인</span>}
              <button type="button" onClick={check} disabled={checking}
                className="text-xs font-semibold border border-indigo-200 text-indigo-600 rounded-md px-3 py-1 hover:bg-indigo-50 disabled:opacity-50 inline-flex items-center gap-1">
                {checking && <Loader2 size={12} className="animate-spin" />}다시 확인
              </button>
            </div>
          </div>
          {!view ? <p className="text-sm text-slate-400 py-2">확인 중…</p> : (
            <>
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-full grid place-items-center ${view.ring}`}>{view.icon}</div>
                <div>
                  <div className={`text-base font-semibold ${view.text}`}>{view.label}</div>
                  <div className="text-xs text-slate-500">{detail}</div>
                </div>
              </div>
              {view.why && (
                <p className={`text-sm rounded-md px-3 py-2.5 leading-relaxed ${st === 'pause' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-800'}`}>{view.why}</p>
              )}
            </>
          )}
        </section>

        {/* 사용량 */}
        <section data-tour="ai-usage" className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-900">사용량</h2>
            <select id="ai-range" aria-label="기간" value={range} onChange={(e) => setRange(e.target.value as Range)}
              className="ml-auto border border-slate-200 rounded-md px-2.5 py-1 text-xs bg-white">
              {(Object.keys(RANGE_LABEL) as Range[]).map((r) => <option key={r} value={r}>{RANGE_LABEL[r]}</option>)}
            </select>
          </div>
          {usageError ? <p className="text-sm text-red-600">사용량을 불러오지 못했습니다 — {usageError}</p> : !t ? <p className="text-sm text-slate-400">불러오는 중…</p> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <Kpi label="호출" value={num(t.calls)} unit="건" />
                <Kpi label="실패" value={num(t.failed)} unit="건" bad={t.failed > 0} />
                <Kpi label="토큰 입력 / 출력" value={short(t.inputTokens)} unit={`/ ${short(t.outputTokens)}`} />
                <Kpi label="평균 걸린 시간" value={t.avgMs == null ? '—' : String(Math.round(t.avgMs / 1000))} unit={t.avgMs == null ? '' : '초'} />
              </div>

              {usage!.daily.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">{RANGE_LABEL[range]} 호출이 없습니다</p>
              ) : (
                <div aria-label={`${RANGE_LABEL[range]} 일별 호출 수`} role="img" className="flex items-end gap-1 h-16 border-b border-slate-200">
                  {usage!.daily.map((d, i) => (
                    <div key={d.day} title={`${d.day} · ${d.calls}건${d.failed ? ` (실패 ${d.failed})` : ''}`}
                      className={`flex-1 min-w-[4px] max-w-[18px] rounded-t-sm ${i === usage!.daily.length - 1 ? 'bg-indigo-600' : 'bg-indigo-300'}`}
                      style={{ height: `${Math.max(6, (d.calls / maxDay) * 100)}%` }} />
                  ))}
                </div>
              )}

              <div>
                <h3 className="text-[13px] font-semibold text-slate-900 mb-2">기능별</h3>
                {usage!.byPurpose.length === 0 ? <p className="text-sm text-slate-400">없음</p> : (
                  <div className="flex flex-col gap-2">
                    {usage!.byPurpose.map((p) => (
                      <div key={p.purpose} className="grid grid-cols-[7rem_minmax(0,1fr)_2.5rem] gap-2 items-center text-[13px] text-slate-700">
                        <span className="truncate">{purposeLabel(p.purpose)}</span>
                        <div className="h-2.5 rounded bg-slate-100 overflow-hidden"><i className="block h-full bg-indigo-300 rounded" style={{ width: `${(p.calls / maxPurpose) * 100}%` }} /></div>
                        <span className="tabular-nums text-right">{num(p.calls)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* 최근 실패 */}
        <section data-tour="ai-fail" className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-900">최근 실패</h2>
            <span className="ml-auto text-xs text-slate-500">최근 10건</span>
          </div>
          {!usage ? null : usage.failures.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">실패한 호출이 없습니다</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="font-medium px-2 py-1.5">시각</th><th className="font-medium px-2 py-1.5">기능</th><th className="font-medium px-2 py-1.5">원인</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.failures.map((f) => (
                    <tr key={f.id} className="border-b border-slate-50 last:border-b-0">
                      <td className="px-2 py-2 whitespace-nowrap tabular-nums text-slate-600">{when(f.at)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-slate-700">{purposeLabel(f.purpose)}</td>
                      <td className="px-2 py-2 text-red-600 min-w-[12rem]">{f.error ?? '원인 기록 없음'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: string; unit: string; bad?: boolean }> = ({ label, value, unit, bad }) => (
  <div className="bg-slate-50 rounded-md px-3 py-2.5 min-w-0">
    <div className="text-[11.5px] text-slate-500">{label}</div>
    <div className={`text-xl font-semibold tabular-nums ${bad ? 'text-red-600' : 'text-slate-900'}`}>
      {value}<small className="text-xs font-normal text-slate-500 ml-0.5">{unit}</small>
    </div>
  </div>
);
